import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { WithBoundArgs } from '@glint/template';
import { restartableTask, timeout } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { not } from 'ember-truth-helpers';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import zft from 'garaje/utils/zero-for-tests';
import { reads } from 'macro-decorators';
import { trackedReset } from 'tracked-toolbox';

type PaginatedModel<T> = PaginatedRecordArray<T> | T[] | null;

interface PaginationSignature<T> {
  Args: {
    limit?: number;
    model?: PaginatedModel<T>;
    page?: number;
    total?: number;
    loadFn?: (page: number, limit: number) => Promise<PaginatedModel<T>>;
    onLoadError?: (error: unknown) => void;
    onPageChange?: (page: number) => void;
    registerApi?: (action: PublicPaginationApi) => void;
  };
  Blocks: {
    default: [PaginationApi<T>];
    header?: [PaginationApi<T>];
    footer?: [PaginationApi<T>];
  };
  Element: HTMLDivElement;
}

export interface PublicPaginationApi {
  goToPage: (page: number) => void;
}

interface PaginationButtonSignature {
  Args: {
    disabled: boolean;
    onClick: () => void;
    icon: string;
    testId: string;
    ariaLabel: string;
  };
  Element: HTMLButtonElement;
}

interface NextButtonSignature {
  Args: {
    hasNextPage: boolean;
    gotoNextPage: () => void;
  };
  Element: HTMLButtonElement;
}

interface PreviousButtonSignature {
  Args: {
    hasPreviousPage: boolean;
    gotoPreviousPage: () => void;
  };
  Element: HTMLButtonElement;
}

interface PaginationApi<T> {
  end: number;
  error: unknown;
  gotoNextPage: () => void;
  gotoPreviousPage: () => void;
  idle: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  loading: boolean;
  maximumPage: number;
  nextButton: WithBoundArgs<typeof NextButton, 'hasNextPage' | 'gotoNextPage'>;
  page: number;
  pageIndicator: string;
  previousButton: WithBoundArgs<typeof PreviousButton, 'hasPreviousPage' | 'gotoPreviousPage'>;
  rows: PaginatedModel<T>;
  start: number;
  total: number;
}

const PaginationButton: TOC<PaginationButtonSignature> = <template>
  <button
    type="button"
    class="flex items-center btn--square mx-2 p-2"
    disabled={{@disabled}}
    aria-label={{@ariaLabel}}
    data-test-pagination-button={{@testId}}
    {{on "click" @onClick}}
    ...attributes
  >
    {{svgJar @icon height="16"}}
  </button>
</template>;

const NextButton: TOC<NextButtonSignature> = <template>
  <PaginationButton
    @disabled={{not @hasNextPage}}
    @onClick={{@gotoNextPage}}
    @icon="arrow-right-large-grayDark"
    @testId="next-page"
    @ariaLabel="Go to next page"
    ...attributes
  />
</template>;

const PreviousButton: TOC<PreviousButtonSignature> = <template>
  <PaginationButton
    @disabled={{not @hasPreviousPage}}
    @onClick={{@gotoPreviousPage}}
    @icon="arrow-left-large-grayDark"
    @testId="previous-page"
    @ariaLabel="Go to previous page"
    ...attributes
  />
</template>;

function getPageInRange(page: number, maximumPage: number): number {
  // When maximumPage is 0 (no data), clamp to page 1 as minimum valid page
  return Math.min(Math.max(page, 1), Math.max(maximumPage, 1));
}

function isUpdatedValue<T>(prev: T | null, current: T): boolean {
  return prev !== undefined && prev !== null && prev !== current;
}

const onUpdate = modifier(
  (
    _: Element,
    [callback]: [(limit: number, total: number, page: number) => void],
    { limit, total, page }: { limit?: number; total?: number; page?: number },
  ) => {
    callback(limit ?? 50, total ?? 0, page ?? 1);
  },
);

/**
 * Pagination Component
 *
 * Provides pagination UI and state management for paginated data.
 *
 * @example
 * ```hbs
 * <Pagination
 *   @page={{this.page}}
 *   @limit={{this.limit}}
 *   @total={{this.total}}
 *   @loadFn={{this.loadDataTask.perform}}
 *   @onPageChange={{fn (mut this.page)}}
 *   as |api|
 * >
 *   <:header as |api|>
 *     <p>{{api.pageIndicator}}</p>
 *     <api.previousButton />
 *     <api.nextButton />
 *   </:header>
 *
 *   <:default as |api|>
 *     {{#each api.rows as |row|}}
 *       <div>{{row.name}}</div>
 *     {{/each}}
 *   </:default>
 *
 *   <:footer as |api|>
 *     <p>{{api.pageIndicator}}</p>
 *     <api.previousButton />
 *     <api.nextButton />
 *   </:footer>
 * </Pagination>
 * ```
 */
export default class PaginationComponent<T> extends Component<PaginationSignature<T>> {
  @reads('args.limit', 50) limit!: number;
  @reads('args.total', 0) total!: number;
  @reads('loadRowsTask.lastSuccessful.value', null) rows!: PaginatedModel<T>;

  @trackedReset<PaginationComponent<T>, number>({
    memo: 'args.page',
    update(component: PaginationComponent<T>): number {
      // Set this.page to a valid page number between 1 and maximumPage (inclusive)
      return getPageInRange(component.args.page || 1, component.maximumPage);
    },
  })
  page = 1;

  @tracked loadError: unknown = null;

  #lastLimit: number | null = null;
  #lastTotal: number | null = null;
  #lastPage: number | null = null;

  constructor(owner: unknown, args: PaginationSignature<T>['Args']) {
    super(owner, args);
    void this.loadRowsTask.perform();

    if (this.args.registerApi) {
      this.args.registerApi({
        goToPage: (page: number) => this.gotoPage(page),
      });
    }
  }

  // clampedPage computes a valid page number between 1 and maximumPage (inclusive)
  // The valid page range can also change if the total or limit changes
  get clampedPage(): number {
    return getPageInRange(this.page, this.maximumPage);
  }

  get start(): number {
    return (this.clampedPage - 1) * this.limit + 1;
  }

  get end(): number {
    return Math.min(this.total, this.clampedPage * this.limit);
  }

  get pageIndicator(): string {
    if (this.loadError) return 'Error.';
    if (this.total === 0) return '';

    return `${this.start}–${this.end} of ${this.total}`;
  }

  get hasPreviousPage(): boolean {
    return this.clampedPage > 1;
  }

  get hasNextPage(): boolean {
    return this.clampedPage < this.maximumPage;
  }

  get maximumPage(): number {
    // We obey the laws of thermodynamics. Don't divide by zero!
    if (this.limit <= 0) return 1;

    // Ensure we have at least one page!
    return Math.max(Math.ceil(this.total / this.limit), 1);
  }

  gotoPage(page: number): void {
    this.page = this.#loadPage(page);
  }

  clampAndGotoPageIfNeeded(page: number): void {
    const clampedPage = getPageInRange(page, this.maximumPage);

    if (page !== clampedPage) this.gotoPage(clampedPage);
  }

  #loadPage(page: number): number {
    page = getPageInRange(page, this.maximumPage);

    // Notify parent if page is out-of-sync
    if (this.args.page !== page) this.args.onPageChange?.(page);

    void this.loadRowsTask.perform(page);

    return page;
  }

  gotoPreviousPage = (): void => {
    this.gotoPage(this.page - 1);
  };

  gotoNextPage = (): void => {
    this.gotoPage(this.page + 1);
  };

  // React to updated page, limit, or total values. This avoids use of didUpdate modifier
  // and Ember run loop (e.g. scheduleOnce).
  reactToArgsChanges = (limit: number, total: number, page: number = this.page): void => {
    const lastLimit = this.#lastLimit;
    const lastTotal = this.#lastTotal;
    const lastPage = this.#lastPage;

    this.#lastLimit = limit;
    this.#lastTotal = total;
    this.#lastPage = page;

    if (isUpdatedValue(lastPage, page)) {
      void this.handleArgsChangeTask.perform(() => {
        void this.loadRowsTask.cancelAll();
        this.page = this.#loadPage(page);
      });
      return;
    }

    if (isUpdatedValue(lastLimit, limit)) {
      void this.handleArgsChangeTask.perform(() => this.gotoPage(this.clampedPage));
      return;
    }

    if (isUpdatedValue(lastTotal, total)) {
      void this.handleArgsChangeTask.perform(() => this.clampAndGotoPageIfNeeded(page));
    }
  };

  handleArgsChangeTask = restartableTask(async (action: () => void) => {
    // Yield to avoid updating during render
    await Promise.resolve();
    action();
  });

  handleLimitChangeTask = restartableTask(async () => {
    // Yield to avoid updating during render
    await Promise.resolve();
    this.gotoPage(this.clampedPage);
  });

  handleTotalChangeTask = restartableTask(async (page: number) => {
    // Yield to avoid updating during render
    await Promise.resolve();
    this.clampAndGotoPageIfNeeded(page);
  });

  loadRowsTask = restartableTask(
    async (page: number = this.page, limit: number = this.limit): Promise<PaginatedModel<T>> => {
      // Slow down for rapid page switching
      if (this.total > 0) await timeout(zft(200));

      this.loadError = null;

      try {
        return (await this.args.loadFn?.(page, limit)) ?? null;
      } catch (error: unknown) {
        this.loadError = error;
        this.args.onLoadError?.(error);

        return null;
      }
    },
  );

  <template>
    <div ...attributes {{onUpdate this.reactToArgsChanges limit=this.limit total=this.total page=@page}}>
      {{#if this.loadError}}
        <div role="alert" aria-live="assertive" class="sr-only">
          Error loading page data
        </div>
      {{/if}}
      {{#let
        (hash
          end=this.end
          error=this.loadError
          gotoNextPage=this.gotoNextPage
          gotoPreviousPage=this.gotoPreviousPage
          idle=this.loadRowsTask.isIdle
          hasNextPage=this.hasNextPage
          hasPreviousPage=this.hasPreviousPage
          loading=this.loadRowsTask.isRunning
          maximumPage=this.maximumPage
          nextButton=(component NextButton hasNextPage=this.hasNextPage gotoNextPage=this.gotoNextPage)
          page=this.page
          pageIndicator=this.pageIndicator
          previousButton=(component
            PreviousButton hasPreviousPage=this.hasPreviousPage gotoPreviousPage=this.gotoPreviousPage
          )
          rows=(if @model @model this.rows)
          start=this.start
          total=this.total
        )
        as |api|
      }}
        {{#if (has-block "header")}}
          {{yield api to="header"}}
        {{/if}}
        {{#if (has-block)}}
          {{yield api}}
        {{/if}}
        {{#if (has-block "footer")}}
          {{yield api to="footer"}}
        {{else}}
          <nav role="navigation" aria-label="Pagination" class="flex justify-end items-center mt-4">
            <p
              class="flex items-center text-carbon-50 my-0 ml-8"
              aria-live="polite"
              aria-atomic="true"
              data-test-pagination-total-count
            >
              {{#if api.idle}}
                <div class="mt-1">
                  {{#if this.total}}
                    {{api.pageIndicator}}
                  {{else}}
                    &nbsp;
                  {{/if}}
                </div>
              {{else}}
                <div class="mt-1" aria-busy="true">
                  <span class="sr-only">Loading page data</span>
                  <span aria-hidden="true">Loading&hellip;</span>
                </div>
              {{/if}}
            </p>
            <p class="flex items-center my-0 ml-8">
              <api.previousButton />
              <api.nextButton />
            </p>
          </nav>
        {{/if}}
      {{/let}}
    </div>
  </template>
}
